<template>
  <div class="review-carousel-container relative">
    <!-- Overall Rating Section -->
    <div v-if="overallRating" class="text-center mb-8">
      <h3 class="text-2xl font-bold text-gray-800">Overall Rating</h3>
      <div class="flex items-center justify-center mt-2">
        <span class="text-xl font-semibold text-gray-700 mr-3">{{ overallRating.toFixed(1) }}</span>
        <span class="text-yellow-400 text-xl flex">
          <font-awesome-icon
              v-for="(icon, i) in displayStarRating(overallRating)"
              :key="`overall-${i}`"
              :icon="icon"
          />
        </span>
      </div>
    </div>

    <!-- Swiper component -->
    <swiper
        :modules="modules"
        :slides-per-view="1"
        :space-between="30"
        :loop="true"
        :autoplay="{
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }"
        :navigation="{
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      }"
        :breakpoints="{
        '640': {
          slidesPerView: 1,
          spaceBetween: 20,
        },
        '768': {
          slidesPerView: 2,
          spaceBetween: 40,
        },
        '1024': {
          slidesPerView: 3,
          spaceBetween: 50,
        },
      }"
        class="py-12 px-4"
    >
      <swiper-slide v-for="(review, index) in localReviews" :key="index">
        <div class="flex flex-col h-full bg-white border border-gray-200 rounded-lg shadow-md p-6 review-card">
          <div class="flex items-start mb-4">
            <img
                class="w-14 h-14 rounded-full mr-4 flex-shrink-0 object-cover"
                :src="`https://picsum.photos/seed/${index}/48`"
                alt="Avatar"
            >
            <div class="w-full">
              <div class="flex justify-between items-start">
                <h4 class="font-bold text-lg text-gray-800 mr-2">{{ review.heading }}</h4>
                <span class="text-yellow-400 text-sm flex-shrink-0 flex">
                   <font-awesome-icon
                       v-for="(icon, i) in displayStarRating(review.rating)"
                       :key="`review-${i}`"
                       :icon="icon"
                   />
                </span>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                {{ review.author }}, {{ review.source }}
              </p>
            </div>
          </div>
          <div class="flex-grow">
            <p class="text-gray-600">
              <span v-if="!review.showFullReview">{{ getShortenedText(review) }}</span>
              <span v-else>{{ review.review }}</span>
            </p>
          </div>
          <div class="mt-4">
            <button
                v-if="isReviewShortened(review)"
                @click="toggleReview(review)"
                class="text-blue-600 hover:underline text-sm font-semibold"
            >
              {{ review.showFullReview ? 'Read Less' : 'Read More' }}
            </button>
            <p class="text-xs text-gray-400 mt-2">
              {{ review.date }}
            </p>
          </div>
        </div>
      </swiper-slide>
    </swiper>
    <!-- Custom Navigation Buttons -->
    <div class="swiper-button-prev absolute top-1/2 -translate-y-1/2 left-2 z-10 cursor-pointer p-2 bg-white rounded-full shadow-md"></div>
    <div class="swiper-button-next absolute top-1/2 -translate-y-1/2 right-2 z-10 cursor-pointer p-2 bg-white rounded-full shadow-md"></div>
  </div>
</template>

<script>
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, A11y, Autoplay } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faStar as fasStar, faStarHalfAlt as fasStarHalfAlt } from "@fortawesome/free-solid-svg-icons";
import { faStar as farStar } from "@fortawesome/free-regular-svg-icons";

import 'swiper/css';
import 'swiper/css/navigation';

export default {
  name: 'ReviewCarousel',
  components: {
    Swiper,
    SwiperSlide,
    FontAwesomeIcon,
  },
  props: {
    reviews: {
      type: Array,
      required: true
    },
    overallRating: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      modules: [Navigation, A11y, Autoplay],
      localReviews: [],
      maxLength: 120,
    };
  },
  watch: {
    reviews: {
      handler(newReviews) {
        this.localReviews = newReviews.map(review => ({
          ...review,
          showFullReview: false
        }));
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // Method to get the correct FontAwesome star icons based on rating
    displayStarRating(rating) {
      const stars = [];
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating % 1 !== 0;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

      for (let i = 0; i < fullStars; i++) stars.push(fasStar);
      if (hasHalfStar) stars.push(fasStarHalfAlt);
      for (let i = 0; i < emptyStars; i++) stars.push(farStar);

      return stars;
    },
    isReviewShortened(review) {
      return review.review.length > this.maxLength;
    },
    getShortenedText(review) {
      if (this.isReviewShortened(review)) {
        return `${review.review.slice(0, this.maxLength)}...`;
      }
      return review.review;
    },
    toggleReview(review) {
      review.showFullReview = !review.showFullReview;
    }
  }
};
</script>

<style scoped>
/* Scoped styles for the component */
.review-carousel-container {
  max-width: 1200px;
  margin: 0 auto;
}

.review-card {
  display: flex;
  flex-direction: column;
  min-height: 280px; /* Ensure cards have a minimum height */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.review-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.swiper-button-next,
.swiper-button-prev {
  color: #1f2937;
  width: 44px;
  height: 44px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  transition: background-color 0.3s ease;
}

.swiper-button-next:hover,
.swiper-button-prev:hover {
  background-color: #f3f4f6;
}

.swiper-button-next::after,
.swiper-button-prev::after {
  font-size: 1.25rem;
  font-weight: bold;
}
</style>
